import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService } from '../../services/auth.service';
import { DialogService } from '../../services/dialog.service';
import { LibraryService } from '../../services/library.service';
import { environment } from '../../../environments/environment';

const BASE_URL = environment.constants.cloudfunctionsURL.replace(/\/$/, '');

const CLOUD_FUNCTION_URLS = {
  GET_FILES: `${BASE_URL}/files-getFiles`,
  UPLOAD_FILES: `${BASE_URL}/files-uploadFiles`,
  DELETE_FILES: `${BASE_URL}/files-deleteFiles`,
  GENERATE_SIGNED_URL: `${BASE_URL}/storage-getSignedUrl`,
  ADD_LINK: `${BASE_URL}/files-addLink`,
  GET_CATEGORIES: `${BASE_URL}/files-getLibraryCategories`
};

@Component({
    selector: 'app-feed-library',
    templateUrl: './feed-library.component.html',
    styleUrls: ['./feed-library.component.scss'],
    standalone: false
})
export class FeedLibraryComponent implements OnInit, OnDestroy {
  lastSelectedIndex: number | null = null;
  selectedItemsSource = new Set<any>();
  selectedItemsTarget1 = new Set<any>();
  isCtrlPressed = false;
  isShiftPressed = false;
  categories: any;

  selectedCategories: any[] = [];
  searchQuery: any;

  originalSourceList = [];

  generalLibraryList = [];

  files: any = [];

  constructor(
    private auth_$: AuthService,
    private http: HttpClient,
    private dialog_$: DialogService,
    private library_$: LibraryService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.loadGeneralLibrary();
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
    window.addEventListener('keyup', this.handleKeyUp.bind(this));
    this.getCategories();
  }

  getIdToken() {
    return this.auth_$.getIdToken();
  }

  async getCategories() {
    // const ownerId = this.auth_$.userData.getValue()['uid'];
    firstValueFrom(this.http
      .post(CLOUD_FUNCTION_URLS.GET_CATEGORIES, {}, { headers: { Authorization: `Bearer ${await this.getIdToken()}` } }))
      .then((response: { message: string; categories: any[] }) => {
        if (!response?.categories) {
          console.error('No categories found');
          this.categories = null;
        }
        this.categories = response.categories;
      })
      .catch(error => {
        console.error('Error fetching categories:', error);
        this.categories = null;
      });
  }

  ngOnDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown.bind(this));
    window.removeEventListener('keyup', this.handleKeyUp.bind(this));
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.isCtrlPressed = true;
    } else if (event.key === 'Shift') {
      this.isShiftPressed = true;
    }
  }

  handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.isCtrlPressed = false;
    } else if (event.key === 'Shift') {
      this.isShiftPressed = false;
    }
  }

  loadGeneralLibrary(folderId?, caseName?) {
    const items = this.library_$.loadHospitalLibrary();
    if (items) {
      items.then((files: any[]) => {
        this.originalSourceList = files;
        this.generalLibraryList = files;
      });
    }
  }

  async openChooseLibraryCategoryDialog(): Promise<{ id: number; name: string } | null> {
    const { ChooseLibraryCategoryComponent } = await import(
      '../ui/choose-library-category/choose-library-category.component'
    );
    return new Promise(resolve => {
      console.log('Categories:', this.categories);
      this.dialog_$
        .open(ChooseLibraryCategoryComponent, {
          width: '600px',
          data: {
            title: 'Choose a category',
            message: 'Choose a category for the library',
            categories: this.categories,
            id: 'choose-library-category'
          }
        })
        .afterClosed()
        .subscribe((category: any) => {
          console.log('Category selected:', category);
          resolve(category || null);
        });
    });
  }

  async openDynamicComponentDialog() {
    const { PromptLinkComponent } = await import('../ui/prompt-link/prompt-link.component');
    this.dialog_$
      .open(PromptLinkComponent, {
        width: '600px',
        data: {
          title: 'Add Link',
          message: 'Add a link to the library',
          id: 'add-link'
        }
      })
      .afterClosed()
      .subscribe((form: { title: string; link: string }) => {
        // TODO: Add the link to the library
        if (form && form.title && form.link) {
          const ownerId = this.auth_$.userData.getValue()['uid'];
          const linkData = {
            ownerId: ownerId,
            title: form.title,
            link: form.link
          };

          this.auth_$.showLoader('Adding link to the library...', 'adding-link');
          firstValueFrom(this.http
            .post(CLOUD_FUNCTION_URLS.ADD_LINK, linkData))
            .then(response => {
              this.auth_$.hideLoader('adding-link');
              // Optionally, reload the library to show the new link
              this.loadGeneralLibrary();
            })
            .catch(error => {
              this.auth_$.hideLoader('adding-link');
              console.error('Error adding link to the library:', error);
            });
        }
      });
  }

  addLink() {
    this.openDynamicComponentDialog();
  }

  async uploadFiles() {
    // Open the choose library category dialog and wait for it to be closed
    /* const selectedCategory = await this.openChooseLibraryCategoryDialog();
    if (!selectedCategory) {
      console.error('No category selected');
      return;
    } */

    // Create a hidden file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);

    // Trigger the file selection dialog
    fileInput.click();

    // Listen for file selection
    fileInput.addEventListener('change', (event: Event) => {
      const input = event.target as HTMLInputElement;
      const files = input.files;
      if (files && files.length > 0) {
        // Prepare FormData to send files
        const formData = new FormData();

        // Add ownerId and category to the form data
        const userData = this.auth_$.userData.getValue() as { uid: string };
        const ownerId = userData?.uid;
        if (ownerId) {
          formData.append('ownerId', ownerId);
          // formData.append('category', selectedCategory?.id.toString());
        } else {
          console.error('No user ID available');
          throw new Error('User ID is required for file upload');
        }

        Array.from(files).forEach(file => {
          formData.append('files', file);
          formData.append('fileSizes', file.size.toString());
          formData.append('fileIcons', this.getFileIcon(file.name));
          formData.append('fileDates', file.lastModified.toString());
        });

        // Now log the complete FormData
        console.log('FormData contents:');
        formData.forEach((value, key) => {
          console.log(key + ': ' + value);
        });

        // Send files to cloud function
        this.auth_$.showLoader('Uploading files...', 'uploading-files');
        firstValueFrom(this.http
          .post(CLOUD_FUNCTION_URLS.UPLOAD_FILES, formData))
          .then(response => {
            console.log('Files uploaded successfully:', response);
            this.auth_$.hideLoader('uploading-files');
            this.loadGeneralLibrary();
          });
      }

      // Clean up: remove the temporary file input
      document.body.removeChild(fileInput);
    });
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
        return 'description';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'image';
      case 'mp4':
      case 'avi':
        return 'movie';
      default:
        return 'insert_drive_file';
    }
  }

  handlePatientFilterFiles($event) {
    this.filterPatientFiles($event);
  }

  handlePatientSearchFiles($event) {
    this.searchPatientFiles($event);
  }

  filterPatientFiles(selectedCategories) {
    this.selectedCategories = selectedCategories;
    const selectedCategoryIds = new Set(selectedCategories.map((category: { id: number }) => category?.id));

    this.generalLibraryList = this.originalSourceList.filter(
      (file: { categoryId: number }) => selectedCategoryIds.size === 0 || selectedCategoryIds.has(file.categoryId)
    );

    // Apply search filter if searchQuery is not empty
    if (this.searchQuery) {
      this.searchPatientFiles(this.searchQuery);
    }
  }

  searchPatientFiles(searchQuery) {
    this.searchQuery = searchQuery.toLowerCase();

    const filterByCategory = file =>
      this.selectedCategories.length === 0 || this.selectedCategories.some(category => category.id === file.categoryId);

    const filterBySearchQuery = file => file.filename.toLowerCase().includes(this.searchQuery);

    if (!searchQuery) {
      // If search query is empty, reset the lists to the filtered lists by category
      this.generalLibraryList = this.originalSourceList.filter(filterByCategory);
    } else {
      // Filter the privateLibraryList and generalLibraryList based on the searchQuery and selected categories

      this.generalLibraryList = this.originalSourceList.filter(
        file => filterBySearchQuery(file) && filterByCategory(file)
      );
    }
  }

  deleteFiles(selectedFiles: Set<any>) {
    const fileIds = Array.from(selectedFiles, file => file.docid);
    this.auth_$.showLoader('Deleting files...', 'deleting-files');

    this.http.post(CLOUD_FUNCTION_URLS.DELETE_FILES, { fileIds }).subscribe({
      next: () => {
        this.auth_$.hideLoader('deleting-files');
        this.loadGeneralLibrary();
      },
      error: error => {
        this.auth_$.hideLoader('deleting-files');
        console.error('Error deleting files: ', error);
      }
    });
  }

  isSelected(item: any, listType: string): boolean {
    if (listType === 'target1') {
      return this.selectedItemsTarget1.has(item);
    }
  }

  toggleSelection(item: any, index: number, listType: string) {
    const selectedItems = listType === 'target1' ? this.selectedItemsTarget1 : new Set();
    const list = listType === 'target1' ? this.generalLibraryList : [];

    if (this.isShiftPressed && this.lastSelectedIndex !== null) {
      const [start, end] = [Math.min(this.lastSelectedIndex, index), Math.max(this.lastSelectedIndex, index)];
      for (let i = start; i <= end; i++) {
        selectedItems.add(list[i]);
      }
    } else if (this.isCtrlPressed) {
      selectedItems.has(item) ? selectedItems.delete(item) : selectedItems.add(item);
    } else {
      selectedItems.clear();
      selectedItems.add(item);
    }
    this.lastSelectedIndex = index;
  }

  async onFileSelected(event: Event) {
    this.auth_$.showLoader('Starting the upload...');

    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        const signedUrl = await this.getSignedUrl(file.name);
        await this.uploadFileToGCS(signedUrl, file);
      }
    }

    this.auth_$.hideLoader();
  }

  private async getSignedUrl(filename: string): Promise<string> {
    const currentUserUID = this.auth_$.userData.getValue()['uid'];
    const response = await firstValueFrom(this.http
      .get<{
        url: string;
      }>(
        `${CLOUD_FUNCTION_URLS.GENERATE_SIGNED_URL}?filename=library/${currentUserUID}/${filename}&signedHeaders=host`,
        { headers: { Authorization: `Bearer ${await this.getIdToken()}` } }
      ));
    return response.url;
  }

  private async uploadFileToGCS(signedUrl: string, file: File): Promise<void> {
    try {
      const response = await firstValueFrom(this.http
        .put(signedUrl, file, {
          headers: {
            'Content-Type': 'application/octet-stream'
          },
          responseType: 'text'
        }));

      console.log('Upload successful:', response);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  }

  async checkAndCreateCloudStorageLocation(cloudStorageLocation: string): Promise<void> {
    try {
      await firstValueFrom(this.http.head(cloudStorageLocation));
      // Location exists, no action needed
    } catch (error) {
      if (error.status === 404) {
        try {
          // If the location does not exist, create it
          await firstValueFrom(this.http.post(cloudStorageLocation, null));
        } catch (createError) {
          console.error('Error creating cloud storage location:', createError);
        }
      } else {
        console.error('Error checking cloud storage location:', error);
      }
    }
  }

  openFile(file) {
    console.log('Opening file:', file);
    this.getSignedUrl(file.filename)
      .then(signedUrl => {
        // Simply open the URL in a new tab
        window.open(signedUrl, '_blank', 'noopener,noreferrer');
      })
      .catch(error => {
        console.error('Error getting signed URL:', error);
      });
  }

  private getContentType(extension: string): string {
    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      default:
        return 'application/octet-stream';
    }
  }
}
